import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Flex, FormControl, Input, Link, Checkbox, Text, VStack, useToast, Select, IconButton } from '@chakra-ui/react';
import { FiArrowLeft } from 'react-icons/fi';
import { checkInfluencerEmail, sendEmailVerificationCode } from '../../services/firebaseService';
import { countryOptions } from 'types';
import { validateEmail, validatePhoneNumber } from 'services/verificationService';
import { formatPhoneNumber } from 'utils/authUtils';
import { authApi } from 'services/api/auth';
import BrandRedirectNotice from './components/BrandRedirectNotice';
import { DisplayHeading } from '../../components/ui';
import { ff } from '../../theme';

const SignUpPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [countryCode, setCountryCode] = useState<string>('+1');
  const [isCrispOpen, setIsCrispOpen] = useState(false);
  const [confirmPhoneNumber, setConfirmPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isBrandEmail, setIsBrandEmail] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [location]);

  const handleSubmit = async () => {
    if (!agreeToTerms) {
      toast({
        title: "Error",
        description: "Please agree to the terms of service to continue.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!firstName || !lastName) {
      toast({
        title: "Error",
        description: "Please enter your first and last names.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!validateEmail(email)) {
      toast({
        title: "Error",
        description: "Please enter a valid email address.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!await checkInfluencerEmail(email)) {
      // No creator account: if the email belongs to a brand dashboard account,
      // point them to the dashboard instead of the generic invite message
      if (await authApi.checkBrandAccount(email)) {
        setIsBrandEmail(true);
        return;
      }
      toast({
        title: "Error",
        description: "Please sign in with the email where you received your invite.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const fullPhoneNumber = formatPhoneNumber(phoneNumber, countryCode);
    const fullConfirmPhoneNumber = formatPhoneNumber(confirmPhoneNumber, countryCode);

    if (fullPhoneNumber !== fullConfirmPhoneNumber) {
      setPhoneError("Phone numbers don't match");
      return;
    }

    if (!validatePhoneNumber(fullPhoneNumber)) {
      setPhoneError("Please enter a valid phone number");
      return;
    }

    setPhoneError('');

    try {
      const isSuccess = await sendEmailVerificationCode(email, fullPhoneNumber, firstName, lastName);
      if (isSuccess) {
        navigate('/email-sent');
      } else {
        toast({
          title: "Error",
          description: "Sign up failed. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Sign up error:', error);
      toast({
        title: "Error",
        description: "An error occurred during sign up. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const toggleCrisp = () => {
    if (isCrispOpen) {
      window.$crisp.push(['do', 'chat:close']);
      window.$crisp.push(['do', 'chat:hide']);
    } else {
      window.$crisp.push(['do', 'chat:show']);
      window.$crisp.push(['do', 'chat:open']);
    }
    setIsCrispOpen(!isCrispOpen);
  };

  return (
    <Box
      minH="100svh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      bg="white"
      px={{ base: '20px', md: 6 }}
      pt={{ base: 4, md: 8 }}
      pb={{ base: 6, md: 10 }}
    >
      <Box width="100%" maxW="440px">
        <IconButton
          aria-label="Back"
          icon={<FiArrowLeft size={20} />}
          onClick={() => navigate('/login')}
          variant="ghost"
          color={ff.ink}
          minW="44px"
          h="44px"
          ml="-10px"
        />
      </Box>

      <VStack
        spacing={8}
        as="form"
        onSubmit={e => { e.preventDefault(); handleSubmit(); }}
        width="100%"
        maxW="440px"
        flex="1"
        align="stretch"
        justifyContent="space-between"
        pt={6}
      >
        {/* Top section */}
        <VStack spacing={4} align="stretch">
          <VStack spacing={2} align="stretch" pb={2}>
            <DisplayHeading size="32px">We're excited to have you!</DisplayHeading>
          </VStack>
          <FormControl isRequired>
            <Input id="first-name" type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name" />
          </FormControl>
          <FormControl isRequired>
            <Input id="last-name" type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name" />
          </FormControl>
          <FormControl isRequired>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                setIsBrandEmail(false);
              }}
              placeholder="Email"
            />
          </FormControl>
          {isBrandEmail && (
            <BrandRedirectNotice
              variant="alert"
              message="This email is linked to a FoodFluence brand account, not a creator account."
            />
          )}
          <FormControl isRequired>
            <Flex gap={2}>
              <Select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                width="31%"
                aria-label="Country code"
              >
                {countryOptions.map(option => (
                  <option key={option.code} value={option.code}>
                    {option.code}
                  </option>
                ))}
              </Select>
              <Input
                type="tel"
                placeholder="Phone number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
            </Flex>
          </FormControl>
          <FormControl isRequired>
            <Input
              type="tel"
              placeholder="Confirm phone number"
              value={confirmPhoneNumber}
              onChange={(e) => setConfirmPhoneNumber(e.target.value)}
            />
          </FormControl>
          {phoneError && <Text fontSize="sm" color={ff.danger}>{phoneError}</Text>}
          <Checkbox id="agree-terms" isChecked={agreeToTerms} onChange={e => setAgreeToTerms(e.target.checked)} minH="44px">
            <Text fontSize="15px" color={ff.body}>
              I agree to the <Link href="https://app.termly.io/policy-viewer/policy.html?policyUUID=e84a5a67-7949-4abf-9a7e-479f3e228317" target="_blank" rel="noopener noreferrer" color={ff.ink} fontWeight={600} textDecoration="underline">terms of service</Link>.
            </Text>
          </Checkbox>
          <Text fontSize="15px" color={ff.text2}>
            Having trouble signing up?{' '}
            <Link as="button" type="button" color={ff.ink} fontWeight={600} textDecoration="underline" onClick={toggleCrisp}>Get help</Link>
          </Text>
        </VStack>

        {/* Bottom section */}
        <VStack spacing={4} align="stretch">
          <Button type="submit" variant="lime" width="full">Register</Button>
          <Text fontSize="15px" color={ff.text2} textAlign="center">
            Already have an account?{' '}
            <Link color={ff.ink} fontWeight={600} textDecoration="underline" href="/returning-user">Log in now</Link>
          </Text>
          <BrandRedirectNotice variant="inline" />
        </VStack>
      </VStack>
    </Box>
  );
};

export default SignUpPage;
